import React from "react";
import { Icons } from "@/assets/images";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { ThemeColorOption, ThemeColorSelectProps } from "./type";

const ThemeColorSelect = ({
  options,
  value,
  onChange,
  onValueChange,
  placeholder = "Select theme color",
  className = "",
  labelName,
  required,
  readOnly,
  errors,
  contentCss,
  isClearable = false,
}: ThemeColorSelectProps): React.ReactElement => {
  const wrapperRef = React.useRef<HTMLDivElement>(null);

  const transformedOptions = options.map((option, index) => ({
    ...option,
    internalValue: `__theme_color_${index}__`,
  }));

  const selectedOption = transformedOptions.find(
    (option) => option.value === value,
  );

  const internalValue = selectedOption?.internalValue ?? "";

  const handleValueChange = (selectedValue: string): void => {
    const selected = transformedOptions.find(
      (option) => option.internalValue === selectedValue,
    );

    (onValueChange ?? onChange)?.(selected?.value ?? "");
  };

  const handleClear = (e: React.PointerEvent | React.MouseEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    (onValueChange ?? onChange)?.("");
  };

  const renderColorOption = (option: ThemeColorOption): React.ReactElement => (
    <div className="flex items-center gap-2.5">
      <span
        className="size-6 rounded-full border border-border shrink-0"
        style={{ backgroundColor: option.value }}
      />
      <span className="truncate">{option.label}</span>
    </div>
  );

  const hasValue = Boolean(value && value !== "");

  return (
    <div
      ref={wrapperRef}
      className={`select-wrapper w-full bg-white ${className}`}
    >
      {labelName && (
        <Label className="mb-1.5 leading-[1.215]">
          {labelName}
          {required && (
            <span className="text-destructive inline-block -ml-1">*</span>
          )}
        </Label>
      )}

      <Select value={internalValue} onValueChange={handleValueChange}>
        <div className="relative">
          <SelectTrigger
            className={`border border-border data-placeholder:text-placeholder text-foreground ${className} ${
              readOnly
                ? "bg-input pointer-events-none focus-visible:border-border"
                : "focus-visible:border-primary"
            }`}
          >
            <SelectValue placeholder={placeholder} className="pr-12">
              {selectedOption ? renderColorOption(selectedOption) : undefined}
            </SelectValue>
          </SelectTrigger>

          {isClearable && hasValue && !readOnly && (
            <button
              type="button"
              className="absolute right-9 top-1/2 z-20 -translate-y-1/2 cursor-pointer bg-white pr-2"
              onPointerDown={handleClear}
              onClick={(e) => e.preventDefault()}
              aria-label="Clear selection"
            >
              <img
                src={Icons.crossIcon}
                alt="clear"
                className="pointer-events-none h-3 w-3 select-down-arrow"
              />
            </button>
          )}
        </div>

        <SelectContent position="popper" className={contentCss}>
          <SelectGroup>
            {transformedOptions.map((option) => (
              <SelectItem
                key={option.internalValue}
                value={option.internalValue}
                className="px-3 py-0 leading-10 hover:cursor-pointer hover:bg-bg-hover"
              >
                {renderColorOption(option)}
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>

      {errors && <p className="mt-1 text-sm text-red-500">{errors}</p>}
    </div>
  );
};

export default ThemeColorSelect;
